/**
 * Organization and Team Management API Service
 *
 * Provides API calls for managing organizations, teams, members, and invitations.
 */
import api from './api';

// ============== Types ==============

// Organization Types
export interface OrganizationMember {
  user_id: string;
  username: string;
  email: string | null;
  display_name: string | null;
  role: 'owner' | 'admin' | 'member';
  joined_at: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  member_count: number;
}

export interface OrganizationDetail extends Organization {
  members: OrganizationMember[];
}

export interface CreateOrganizationRequest {
  name: string;
  description?: string;
}

export interface UpdateOrganizationRequest {
  name?: string;
  description?: string;
}

// Team Types
export interface TeamMember {
  user_id: string;
  username: string;
  email: string | null;
  display_name: string | null;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  joined_at: string;
}

export interface Team {
  id: string;
  name: string;
  organization_id: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  member_count: number;
}

export interface TeamDetail extends Team {
  members: TeamMember[];
}

export interface CreateTeamRequest {
  name: string;
  description?: string;
  organization_id: string;
}

export interface UpdateTeamRequest {
  name?: string;
  description?: string;
}

// Invite Types
export interface SiteInvite {
  id: string;
  email: string;
  status: string;
  invite_code: string;
  invite_url: string;
  created_at: string;
  expires_at: string;
  email_sent: boolean;
  invited_by_name: string | null;
}

export interface OrgInvite {
  id: string;
  email: string;
  organization_id: string;
  organization_name: string;
  team_id: string | null;
  team_name: string | null;
  org_role: 'owner' | 'admin' | 'member';
  team_role: 'owner' | 'admin' | 'member' | 'viewer';
  status: string;
  invite_code: string;
  invite_url: string;
  created_at: string;
  expires_at: string;
  email_sent: boolean;
  invited_by_name: string | null;
}

export interface CreateOrgInviteRequest {
  email: string;
  team_id?: string;
  org_role?: 'owner' | 'admin' | 'member';
  team_role?: 'owner' | 'admin' | 'member' | 'viewer';
}

// ============== Organizations API ==============

/**
 * List all organizations the current user is a member of.
 */
export async function listOrganizations(): Promise<Organization[]> {
  const response = await api.get<Organization[]>('/organizations');
  return response.data;
}

/**
 * Get a specific organization by ID with member list.
 */
export async function getOrganization(orgId: string): Promise<OrganizationDetail> {
  const response = await api.get<OrganizationDetail>(`/organizations/${orgId}`);
  return response.data;
}

/**
 * Create a new organization. User becomes owner.
 */
export async function createOrganization(data: CreateOrganizationRequest): Promise<Organization> {
  const response = await api.post<Organization>('/organizations', data);
  return response.data;
}

/**
 * Update an organization (admin+ required).
 */
export async function updateOrganization(orgId: string, data: UpdateOrganizationRequest): Promise<Organization> {
  const response = await api.put<Organization>(`/organizations/${orgId}`, data);
  return response.data;
}

/**
 * Archive/delete an organization (owner only).
 */
export async function deleteOrganization(orgId: string): Promise<void> {
  await api.delete(`/organizations/${orgId}`);
}

// ============== Organization Members API ==============

/**
 * List members of an organization.
 */
export async function listOrgMembers(orgId: string): Promise<OrganizationMember[]> {
  const response = await api.get<OrganizationMember[]>(`/organizations/${orgId}/members`);
  return response.data;
}

/**
 * Add a member to an organization by email (admin+ required).
 */
export async function addOrgMember(orgId: string, data: { email: string; role: string }): Promise<OrganizationMember> {
  const response = await api.post<OrganizationMember>(`/organizations/${orgId}/members`, data);
  return response.data;
}

/**
 * Update a member's role in an organization (owner only).
 */
export async function updateOrgMemberRole(orgId: string, userId: string, role: string): Promise<OrganizationMember> {
  const response = await api.put<OrganizationMember>(`/organizations/${orgId}/members/${userId}`, { role });
  return response.data;
}

/**
 * Remove a member from an organization (admin+ or self).
 */
export async function removeOrgMember(orgId: string, userId: string): Promise<void> {
  await api.delete(`/organizations/${orgId}/members/${userId}`);
}

/**
 * List teams in an organization.
 */
export async function listOrgTeams(orgId: string): Promise<Team[]> {
  const response = await api.get<Team[]>(`/organizations/${orgId}/teams`);
  return response.data;
}

// ============== Teams API ==============

/**
 * List all teams the current user is a member of.
 */
export async function listTeams(organizationId?: string): Promise<Team[]> {
  const params = organizationId ? { organization_id: organizationId } : {};
  const response = await api.get<Team[]>('/teams', { params });
  return response.data;
}

/**
 * Get a specific team by ID with member list.
 */
export async function getTeam(teamId: string): Promise<TeamDetail> {
  const response = await api.get<TeamDetail>(`/teams/${teamId}`);
  return response.data;
}

/**
 * Create a new team within an organization. User becomes owner.
 */
export async function createTeam(data: CreateTeamRequest): Promise<Team> {
  const response = await api.post<Team>('/teams', data);
  return response.data;
}

/**
 * Update a team (team admin+ required).
 */
export async function updateTeam(teamId: string, data: UpdateTeamRequest): Promise<Team> {
  const response = await api.put<Team>(`/teams/${teamId}`, data);
  return response.data;
}

/**
 * Delete a team (team owner only).
 */
export async function deleteTeam(teamId: string): Promise<void> {
  await api.delete(`/teams/${teamId}`);
}

// ============== Team Members API ==============

/**
 * List members of a team.
 */
export async function listTeamMembers(teamId: string): Promise<TeamMember[]> {
  const response = await api.get<TeamMember[]>(`/teams/${teamId}/members`);
  return response.data;
}

/**
 * Add a member to a team (team admin+ required).
 */
export async function addTeamMember(teamId: string, data: { email: string; role: string }): Promise<TeamMember> {
  const response = await api.post<TeamMember>(`/teams/${teamId}/members`, data);
  return response.data;
}

/**
 * Update a member's role in a team (team owner only).
 */
export async function updateTeamMemberRole(teamId: string, userId: string, role: string): Promise<TeamMember> {
  const response = await api.put<TeamMember>(`/teams/${teamId}/members/${userId}`, { role });
  return response.data;
}

/**
 * Remove a member from a team (team admin+ or self).
 */
export async function removeTeamMember(teamId: string, userId: string): Promise<void> {
  await api.delete(`/teams/${teamId}/members/${userId}`);
}

// ============== Site Invites API (Superadmin only) ==============

/**
 * List site-level invites (superadmin only).
 */
export async function listSiteInvites(status?: string): Promise<SiteInvite[]> {
  const params = status ? { status } : {};
  const response = await api.get<SiteInvite[]>('/invites/site', { params });
  return response.data;
}

/**
 * Create a site-level invite (superadmin only).
 */
export async function createSiteInvite(email: string): Promise<SiteInvite> {
  const response = await api.post<SiteInvite>('/invites/site', { email });
  return response.data;
}

/**
 * Revoke a site-level invite (superadmin only).
 */
export async function revokeSiteInvite(inviteId: string): Promise<void> {
  await api.delete(`/invites/site/${inviteId}`);
}

/**
 * Resend a site-level invite email (superadmin only).
 */
export async function resendSiteInvite(inviteId: string): Promise<void> {
  await api.post(`/invites/site/${inviteId}/resend`);
}

// ============== Organization Invites API ==============

/**
 * List organization invites (org admin+ required).
 */
export async function listOrgInvites(status?: string): Promise<OrgInvite[]> {
  const params = status ? { status } : {};
  const response = await api.get<OrgInvite[]>('/invites/org', { params });
  return response.data;
}

/**
 * Create an organization invite (org admin+ required).
 */
export async function createOrgInvite(data: CreateOrgInviteRequest): Promise<OrgInvite> {
  const response = await api.post<OrgInvite>('/invites/org', data);
  return response.data;
}

/**
 * Revoke an organization invite (org admin+ required).
 */
export async function revokeOrgInvite(inviteId: string): Promise<void> {
  await api.delete(`/invites/org/${inviteId}`);
}

/**
 * Resend an organization invite email (org admin+ required).
 */
export async function resendOrgInvite(inviteId: string): Promise<void> {
  await api.post(`/invites/org/${inviteId}/resend`);
}

// ============== Role Constants ==============

export const ORG_ROLES = [
  { value: 'owner', label: 'Owner', description: 'Full control over organization' },
  { value: 'admin', label: 'Admin', description: 'Can manage members and teams' },
  { value: 'member', label: 'Member', description: 'Standard access' },
] as const;

export const TEAM_ROLES = [
  { value: 'owner', label: 'Owner', description: 'Full control over team' },
  { value: 'admin', label: 'Admin', description: 'Can manage team members' },
  { value: 'member', label: 'Member', description: 'Standard access' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access' },
] as const;

export type OrgRole = typeof ORG_ROLES[number]['value'];
export type TeamRole = typeof TEAM_ROLES[number]['value'];
